import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import {
  bannerImages,
  bannerStyleShown,
  clipKindLabel,
  crawlEntries,
  currentLogoIndex,
  formatSpotTime,
  linkLabel,
  lshapeItemAt,
  lshapeItems,
  lshapePosition,
  rotatingLogos,
  sponsorLogo,
  type AdBannerConfig,
  type AdBreak,
  type AdFrame,
  type AdFrameKind,
  type AdLShapeConfig,
  type AdsState,
  type CornerSlot,
  type LShapeItem,
  type PlacedMaterial,
  type Sponsor,
  type SponsorLogoConfig,
  type SponsorMaterial,
  type SponsorPlate,
} from '@rabbitfuel/shared';
import { inkFor, useSponsorColor } from '../lib/sponsorColor.ts';
import { QrCode } from './QrCode.tsx';

/*
 * Werbung im Player: das Sponsor-Logo im Bild und die Werbepause.
 *
 * Beides ist HTML ueber dem Bild, wie die Einblendungen -- und kommt deshalb
 * genauso in den RTMP-Ausgang: der Server nimmt die fertig gesetzte
 * Renderseite auf. Zuschauersicht, Regie-Monitor und Renderseite benutzen
 * dieselben Bausteine von hier, damit alle drei dasselbe zeigen.
 */

/* ----------------------------------------------------------- Werbepause */

export interface AdBreakView {
  /** Die Pause, solange sie *hier* laeuft -- ist der Spot zu Ende, null. */
  active: AdBreak | null;
  /** Wo der Spot gerade stehen muesste, in ms. */
  position: () => number;
  /** Der Spot ist hier fertig -- vor der Meldung des Servers. */
  onEnded: () => void;
}

/**
 * Die laufende Werbepause, gesehen von diesem Geraet.
 *
 * Gezaehlt wird ab Eingang der Nachricht mit der eigenen Uhr, nicht gegen
 * einen Zeitstempel des Servers -- siehe `AdBreak.elapsedMs`. Und es gibt
 * zwei Wege zurueck ins Programm: der Spot endet hier (`ended`), oder der
 * Server meldet das Ende. Kommt keins von beiden -- Netz weg --, schaltet
 * der Player nach Spotlaenge plus Luft selbst zurueck, statt ewig auf einem
 * stehenden Spot zu verharren.
 */
export function useAdBreak(ads: AdsState, receivedAt: number): AdBreakView {
  const running = ads.break;
  const [doneId, setDoneId] = useState<string | null>(null);

  useEffect(() => {
    if (!running) return;
    const left = running.durationMs + 2000 - running.elapsedMs - (performance.now() - receivedAt);
    const timer = window.setTimeout(() => setDoneId(running.id), Math.max(0, left));
    return () => window.clearTimeout(timer);
  }, [running, receivedAt]);

  const runningId = running?.id;
  const onEnded = useCallback(() => {
    if (runningId) setDoneId(runningId);
  }, [runningId]);
  const position = useCallback(
    () => (running ? running.elapsedMs + Math.max(0, performance.now() - receivedAt) : 0),
    [running, receivedAt],
  );

  return { active: running && doneId !== running.id ? running : null, position, onEnded };
}

/**
 * Das Video der Werbepause. Steht *immer* im Baum, auch ohne Pause -- aus
 * demselben Grund wie das <audio> des kleinen Fensters in Watch.tsx: iOS
 * spielt Ton nur aus einem Element, das schon beim Tipp auf „Ton an" da war.
 * Ein erst zur Pause eingehaengtes Video bliebe dort stumm.
 *
 * Wer spaeter dazukommt, springt an die Stelle, an der die anderen gerade
 * sind -- deshalb muss der Server Teilabrufe (Range) koennen.
 */
export function AdSpot({
  ad,
  muted,
  videoRef,
  sinkId,
  onSoundBlocked,
}: {
  ad: AdBreakView;
  muted: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Ausgabegeraet -- in der Regie dasselbe wie fuers Gegensprechen. */
  sinkId?: string;
  /** Der Browser hat den Spot nur stumm starten lassen. */
  onSoundBlocked?: () => void;
}) {
  const { active, position } = ad;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const blockedRef = useRef(onSoundBlocked);
  blockedRef.current = onSoundBlocked;
  const positionRef = useRef(position);
  positionRef.current = position;

  const spotId = active?.id ?? null;
  const spotUrl = active?.spotUrl ?? null;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!spotId || !spotUrl) {
      // Anhalten *und* die Quelle loesen -- sonst laedt das Handy weiter.
      if (el.getAttribute('src')) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      return;
    }

    let alive = true;
    el.src = spotUrl;
    el.muted = mutedRef.current;
    const seek = () => {
      const at = positionRef.current() / 1000;
      if (at > 0.5 && Number.isFinite(el.duration) && at < el.duration - 0.25) el.currentTime = at;
    };
    el.addEventListener('loadedmetadata', seek, { once: true });
    el.play().catch((err: unknown) => {
      if (!alive || (err as { name?: string })?.name !== 'NotAllowedError' || el.muted) return;
      // Mit Ton verweigert (iOS ohne frischen Tipp): stumm laufen lassen und
      // den Ton-Knopf anbieten -- ein stehender Spot waere schlechter.
      el.muted = true;
      blockedRef.current?.();
      void el.play().catch(() => undefined);
    });
    return () => {
      alive = false;
      el.removeEventListener('loadedmetadata', seek);
    };
  }, [spotId, spotUrl, videoRef]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoRef]);

  useEffect(() => {
    const el = videoRef.current as (HTMLVideoElement & { setSinkId?: (id: string) => Promise<void> }) | null;
    if (sinkId && el?.setSinkId) void el.setSinkId(sinkId).catch(() => undefined);
  }, [sinkId, videoRef]);

  return <video ref={videoRef} className="ad-spot" playsInline preload="none" onEnded={ad.onEnded} />;
}

/**
 * Pflichtkennzeichnung plus Restzeit. Werbung muss im Stream als solche zu
 * erkennen sein -- deshalb steht das Wort, solange der Spot laeuft, und ist
 * nicht abschaltbar. Die Restzeit tickt hier drin und nicht im Player, damit
 * nicht die ganze Seite viermal pro Sekunde neu rendert.
 */
export function AdLabel({
  ad,
  live,
  note,
}: {
  ad: AdBreakView;
  /** Laeuft daneben ein Live-Bild? Nur dann bekommt die Ecke ihr Schild. */
  live: boolean;
  /** Zusatzzeile -- nur im Regie-Monitor. */
  note?: string;
}) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!ad.active) return;
    const timer = window.setInterval(() => tick((n) => n + 1), 250);
    return () => window.clearInterval(timer);
  }, [ad.active]);
  if (!ad.active) return null;
  const left = ad.active.durationMs - ad.position();
  const clip = ad.active.clip;

  /*
   * Eine Botschaft ist keine Werbung -- also kein „Werbung", sondern ein
   * Namensschild unten im Bild: wer spricht, und wie lange noch. Bei einer
   * Sprachnachricht gibt es kein Bild; dann steht in der Mitte eine Karte
   * mit Name und Tonwelle, damit das Bild nicht schwarz ist.
   */
  if (clip) {
    return (
      <>
        {clip.kind === 'audio' && (
          <div className="clip-audio-card" aria-hidden>
            <span className="clip-audio-card__icon">🎤</span>
            <b>{clip.from}</b>
            <span className="clip-audio-card__wave">
              {Array.from({ length: 9 }, (_, i) => (
                <i key={i} style={{ animationDelay: `${(i % 5) * 0.12}s` }} />
              ))}
            </span>
          </div>
        )}
        <div className="clip-label" role="status">
          <span className="clip-label__kind">
            {clipKindLabel[clip.kind]} <span>{formatSpotTime(left)}</span>
          </span>
          <b className="clip-label__from">{clip.from}</b>
          {note && <em>{note}</em>}
        </div>
        {live && (
          <span className="ad-live-tag">
            <span className="dot dot--pulse" />
            Live
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <div className="ad-label" role="status">
        <b>Werbung</b>
        <span>{formatSpotTime(left)}</span>
        {note && <em>{note}</em>}
      </div>
      {live && (
        <span className="ad-live-tag">
          <span className="dot dot--pulse" />
          Live
        </span>
      )}
    </>
  );
}

/** Gegenueberliegende Ecke derselben Hoehe. */
const MIRROR: Record<CornerSlot, CornerSlot> = {
  topLeft: 'topRight',
  topRight: 'topLeft',
  bottomLeft: 'bottomRight',
  bottomRight: 'bottomLeft',
};
/** Wo das Namensschild einer Botschaft am liebsten steht -- unten links zuerst. */
const LABEL_SLOTS: CornerSlot[] = ['bottomLeft', 'bottomRight', 'topLeft', 'topRight'];

/**
 * Die `data-`Attribute fuer den Player waehrend einer Werbepause. Das
 * Stylesheet stellt damit um: Spot formatfuellend, das Live-Bild klein in die
 * gewaehlte Ecke, Einblendungen und kleines Fenster weg. Das Hauptbild-Video
 * bleibt dabei *dasselbe Element* -- es rutscht nur in die Ecke, statt neu
 * verbunden zu werden. So steht das Live-Bild nach der Pause sofort wieder da.
 *
 * Bei einer Botschaft bleibt das Sponsor-Logo im Bild -- sie ist Programm,
 * keine Werbung, und die Sponsoren haben fuer die Sendezeit bezahlt. Dann
 * teilen sich drei Dinge die vier Ecken: das Logo bleibt, wo die Regie es
 * hingestellt hat; will das Live-Bild in dieselbe Ecke, weicht es auf die
 * andere Seite aus; das Namensschild nimmt die erste freie Ecke.
 */
export function adChrome(ad: AdBreakView, ads: AdsState, live: boolean) {
  const clip = Boolean(ad.active?.clip);
  const logo = clip && ads.logo.on && rotatingLogos(ads).length > 0 ? ads.logo.slot : null;
  const liveSlot = logo && live && ads.live.slot === logo ? MIRROR[logo] : ads.live.slot;
  const taken = new Set<CornerSlot | null>([logo, live ? liveSlot : null]);
  return {
    'data-adbreak': ad.active ? 'on' : 'off',
    'data-adbreak-kind': clip ? 'clip' : 'spot',
    // Ohne Live-Bild bliebe in der Ecke ein leerer Rahmen stehen.
    'data-ad-live': live ? 'on' : 'off',
    'data-ad-live-slot': liveSlot,
    'data-ad-live-size': ads.live.size,
    'data-clip-label': LABEL_SLOTS.find((s) => !taken.has(s)) ?? 'bottomLeft',
  };
}

/* ------------------------------------------------------- Logo im Bild */

/** Was die Einblendungs-Ebene braucht, um das Logo zu zeigen -- oder null. */
export interface SponsorBugProps {
  config: SponsorLogoConfig;
  /** Alle Bilder mit Haekchen „Logo im Bild" -- ueber alle Sponsoren. */
  logos: PlacedMaterial[];
}

export function sponsorBug(ads: AdsState): SponsorBugProps | null {
  const logos = rotatingLogos(ads);
  return ads.logo.on && logos.length ? { config: ads.logo, logos } : null;
}

/**
 * Das Sponsor-Logo. Mehrere wechseln sich ab; welches dran ist, ergibt sich
 * aus der Uhrzeit (`currentLogoIndex`), damit alle Zuschauer gleichzeitig
 * wechseln. Alle Logos liegen uebereinander im selben Rasterfeld: so sind
 * sie vorgeladen, die Flaeche bekommt die Groesse des groessten, und der
 * Wechsel ist ein Ueberblenden statt eines Sprungs.
 */
export function SponsorBug({ config, logos }: SponsorBugProps) {
  const [index, setIndex] = useState(() => currentLogoIndex(logos.length, config.rotateSeconds));

  useEffect(() => {
    setIndex(currentLogoIndex(logos.length, config.rotateSeconds));
    if (logos.length < 2) return;
    // Jede Sekunde nachsehen reicht -- gewechselt wird an vollen Takten.
    const timer = window.setInterval(() => setIndex(currentLogoIndex(logos.length, config.rotateSeconds)), 1000);
    return () => window.clearInterval(timer);
  }, [logos.length, config.rotateSeconds]);

  return (
    <div className={`so-sponsor so-sponsor--${config.size} so-sponsor--${config.plate}`}>
      {logos.map(({ sponsor, material }, i) => (
        <img
          key={material.id}
          src={material.url}
          alt={i === index ? sponsor.name : ''}
          aria-hidden={i === index ? undefined : true}
          className={i === index ? 'is-on' : undefined}
          draggable={false}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------- Banner und L-Form */

/** So lange fahren Banner und L herein und hinaus -- dieselbe Zeit wie `--af-move` im Stylesheet. */
const FRAME_MOVE_MS = 700;

/**
 * Was gerade als Banner oder L im Bild steht. Die Lage (Kante, Ecke, Staerke)
 * wird beim Hereinfahren festgehalten: so faehrt es beim Hinausfahren dorthin
 * zurueck, woher es kam, auch wenn die Regie es inzwischen gedreht hat.
 */
export interface AdFrameScene {
  key: string;
  frame: AdFrame;
  kind: AdFrameKind;
  banner: AdBannerConfig;
  lshape: AdLShapeConfig;
  /** Wann `frame` ankam -- ab da zaehlt die L-Form ihre Spots mit der eigenen Uhr. */
  receivedAt: number;
}

export interface AdFrameView {
  scene: AdFrameScene | null;
  /** Im Bild (true) -- oder gerade auf dem Weg hinein bzw. hinaus. */
  visible: boolean;
}

/**
 * Banner oder L, gesehen von diesem Geraet -- mit Fahrt hinein und hinaus.
 *
 * Laeuft eine Werbepause oder Botschaft (`suppressed`), tritt es solange
 * zurueck: der Spot hat dann das ganze Bild. Danach kommt es wieder.
 *
 * Die Lage gehoert zur Identitaet. Dreht die Regie das L in eine andere Ecke
 * oder legt das Banner nach oben, faehrt es erst hinaus und dann an der
 * neuen Stelle herein -- ein Sprung von links nach rechts saehe im Stream
 * wie ein Fehler aus.
 */
export function useAdFrame(ads: AdsState, receivedAt: number, suppressed: boolean): AdFrameView {
  const target = !suppressed ? ads.frame : null;
  const geometry = !target
    ? ''
    : target.kind === 'banner'
      ? `${ads.banner.edge}:${ads.banner.size}`
      : `${ads.lshape.corner}:${ads.lshape.size}`;
  const key = target ? `${target.id}:${target.kind}:${geometry}` : null;

  const [scene, setScene] = useState<AdFrameScene | null>(null);
  const [visible, setVisible] = useState(false);
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const latest = useRef({ ads, receivedAt, target });
  latest.current = { ads, receivedAt, target };

  useEffect(() => {
    let raf = 0;
    let timer = 0;
    const enter = () => {
      const { ads: now, receivedAt: at, target: frame } = latest.current;
      if (!key || !frame) return setScene(null);
      setScene({ key, frame, kind: frame.kind, banner: now.banner, lshape: now.lshape, receivedAt: at });
      // Erst im uebernaechsten Bild sichtbar: sonst stuende es ohne Fahrt sofort da.
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => setVisible(true));
      });
    };
    setVisible(false);
    if (!sceneRef.current) enter();
    else timer = window.setTimeout(enter, FRAME_MOVE_MS);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [key]);

  /*
   * Die Lage bleibt die vom Hereinfahren -- Stand und Uhr aber sind die
   * neuesten. Nur so kommen „Weiter" und „Halten" an, ohne dass das L
   * hinaus- und wieder hereinfaehrt.
   */
  const live = scene && target && target.id === scene.frame.id ? { ...scene, frame: target, receivedAt } : scene;
  return { scene: live, visible };
}

/**
 * Die `data-`Attribute fuer den Player. `data-adframe` sagt, ob Banner oder L
 * gerade im Bild stehen -- daran rueckt das Live-Bild zusammen. Die uebrigen
 * bleiben auch beim Hinausfahren stehen, damit Banner und L wissen, wohin.
 */
export function adFrameChrome({ scene, visible }: AdFrameView) {
  const corner = scene?.lshape.corner ?? 'bottomLeft';
  return {
    'data-adframe': scene && visible ? scene.kind : 'off',
    'data-af-kind': scene?.kind ?? 'off',
    'data-af-size': scene ? (scene.kind === 'banner' ? scene.banner.size : scene.lshape.size) : 'md',
    'data-af-edge': scene?.banner.edge ?? 'bottom',
    'data-af-x': corner === 'bottomLeft' || corner === 'topLeft' ? 'left' : 'right',
    'data-af-y': corner === 'bottomLeft' || corner === 'bottomRight' ? 'bottom' : 'top',
  };
}

/**
 * Banner oder L selbst. Liegt neben dem Programm, nicht darin: das Programm
 * schrumpft, die Werbung fuellt den frei gewordenen Rand. Inhalt (Sponsoren,
 * Werbetexte, Stil) kommt aus dem aktuellen Stand -- ein neuer Werbetext steht
 * also sofort im laufenden Banner.
 */
export function AdFrameLayer({ view, ads }: { view: AdFrameView; ads: AdsState }) {
  const { scene } = view;
  if (!scene) return null;
  return scene.kind === 'banner' ? <AdBanner ads={ads} /> : <AdLShape ads={ads} scene={scene} />;
}

/** Ein Bild auf der Flaeche, die die Regie fuers Logo im Bild gewaehlt hat. */
function PlatedLogo({ url, name, plate }: { url: string; name: string; plate: SponsorPlate }) {
  return (
    <span className={`adframe__logo adframe__logo--${plate}`}>
      <img src={url} alt={name} draggable={false} />
    </span>
  );
}

function AdBanner({ ads }: { ads: AdsState }) {
  const style = bannerStyleShown(ads);
  return (
    <div className="adframe adframe--banner" data-look={ads.banner.look} role="complementary" aria-label="Werbung">
      {/* Eigene Ebene, damit die Schrift sich an der Hoehe des Banners messen kann. */}
      <div className="adframe__bar">
        <span className="adframe__tag">Werbung</span>
        {style === 'images' ? (
          <BannerImages items={bannerImages(ads)} rotateSeconds={ads.banner.rotateSeconds} />
        ) : (
          <Crawl entries={crawlEntries(ads)} plate={ads.logo.plate} />
        )}
      </div>
    </div>
  );
}

/** Wie lange ein Eintrag braucht, um eine Bannerbreite weit zu rollen. Ruhig genug zum Lesen. */
const CRAWL_SECONDS_PER_WIDTH = 14;

/**
 * Das Laufband: die Bannerbilder und der Werbetext jedes Sponsors rollen ohne
 * Pause von rechts nach links.
 *
 * Die Liste steht zweimal hintereinander, und die Animation schiebt genau um
 * eine Liste weiter -- dann sieht der Sprung zurueck aus wie das Weiterlaufen.
 * Jede Liste ist mindestens so breit wie das Band, sonst klaffte bei nur einem
 * Sponsor eine Luecke.
 *
 * Die Stelle im Band rechnet sich aus der Uhrzeit (negativer Animationsbeginn)
 * -- wie beim Logo-Wechsel: alle Zuschauer sehen ungefaehr denselben Sponsor.
 */
function Crawl({ entries, plate }: { entries: { sponsor: Sponsor; images: SponsorMaterial[] }[]; plate: SponsorPlate }) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [timing, setTiming] = useState<{ duration: number; delay: number } | null>(null);
  const content = entries
    .map(
      ({ sponsor, images }) =>
        `${sponsor.id}:${sponsor.tagline}:${sponsor.cta}:${sponsor.link}:${images.map((m) => m.url).join(',')}`,
    )
    .join('|');

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const list = track.firstElementChild as HTMLElement | null;
      const view = track.parentElement?.clientWidth ?? 0;
      if (!list || !view || !list.offsetWidth) return;
      // In Bandbreiten statt Pixeln: auf Handy und Beamer gleich schnell.
      const duration = Math.max(6, (list.offsetWidth / view) * CRAWL_SECONDS_PER_WIDTH);
      setTiming((prev) =>
        prev && Math.abs(prev.duration - duration) < 0.05 ? prev : { duration, delay: -((Date.now() / 1000) % duration) },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [content]);

  const list = (copy: number) => (
    <div className="crawl__list" aria-hidden={copy > 0 ? true : undefined}>
      {entries.map(({ sponsor, images }) => (
        <span className="crawl__item" key={sponsor.id}>
          {images.map((image) => (
            <PlatedLogo key={image.id} url={image.url} name={sponsor.name} plate={plate} />
          ))}
          {sponsor.tagline && <span className="crawl__text">{sponsor.tagline}</span>}
          {(sponsor.cta || sponsor.link) && (
            <span className="crawl__cta">{sponsor.cta || linkLabel(sponsor.link)}</span>
          )}
        </span>
      ))}
    </div>
  );

  return (
    <div className="adframe__crawl">
      <div
        className={`crawl__track${timing ? ' is-running' : ''}`}
        ref={trackRef}
        style={
          timing
            ? ({ '--crawl-duration': `${timing.duration}s`, '--crawl-delay': `${timing.delay}s` } as CSSProperties)
            : undefined
        }
      >
        {list(0)}
        {list(1)}
      </div>
    </div>
  );
}

/** Bannerbilder, eins nach dem anderen -- gewechselt nach der Uhrzeit wie die Logos. */
function BannerImages({ items, rotateSeconds }: { items: PlacedMaterial[]; rotateSeconds: number }) {
  const [index, setIndex] = useState(() => currentLogoIndex(items.length, rotateSeconds));
  useEffect(() => {
    setIndex(currentLogoIndex(items.length, rotateSeconds));
    if (items.length < 2) return;
    const timer = window.setInterval(() => setIndex(currentLogoIndex(items.length, rotateSeconds)), 1000);
    return () => window.clearInterval(timer);
  }, [items.length, rotateSeconds]);

  return (
    <div className="adframe__images">
      {items.map(({ sponsor, material }, i) => (
        <img
          key={material.id}
          src={material.url}
          alt={i === index ? sponsor.name : ''}
          aria-hidden={i === index ? undefined : true}
          className={i === index ? 'is-on' : undefined}
          draggable={false}
        />
      ))}
    </div>
  );
}

/**
 * Die L-Form. Das angehakte Material laeuft reihum; welches Stueck dran ist,
 * rechnet jeder Zuschauer aus der Stelle in der Reihe (`lshapePosition`) --
 * „Weiter" und „Halten" der Regie verschieben bzw. frieren diese Stelle ein.
 *
 * Das L ist *ein* Stueck: eine Flaeche ueber beide Streifen, die als Ganzes
 * schraeg hereinfaehrt -- genau so weit, wie das Live-Bild zusammenrueckt.
 * Das Live-Bild liegt als Fenster darauf (Stylesheet: runde Innenecke,
 * Schatten, rote Kante). Darin:
 *
 * - **Ecke**: ein Video stumm oder ein Eckbild, wie ein eingelassener
 *   Bildschirm -- weiter in 16:9, denn die Ecke ist es auch.
 * - **Seitenstreifen**: das Logo gross. Steht es schon als Eckbild da oder
 *   gibt es keins, steht dort der Name.
 * - **Kantenstreifen**: „Werbung", der Werbetext gross, Fortschritt und
 *   rechts Hinweis und QR-Code. Ohne Werbetext steht dort der Name -- aber
 *   nur, wenn er nicht schon im Seitenstreifen steht.
 * - **L-Grafik**: liegt ueber allem und ersetzt Logo und Text; nur die
 *   Kennzeichnung bleibt.
 *
 * Im Look „Sponsorfarbe" steht das L in der Farbe des Sponsors, der gerade
 * dran ist, und wechselt weich mit ihm. Hat er keine erkennbare, bleibt es
 * dunkel.
 */
function AdLShape({ ads, scene }: { ads: AdsState; scene: AdFrameScene }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((n) => n + 1), 250);
    return () => window.clearInterval(timer);
  }, []);
  const items = lshapeItems(ads, scene.frame);
  const at = lshapeItemAt(items, lshapePosition(scene.frame, scene.receivedAt));
  const sponsor = at?.item.sponsor ?? null;
  const material = at?.item.material ?? null;
  const graphic = material?.kind === 'image' && material.lshapeStyle === 'full' ? at!.item : null;
  const logoUrl = sponsor ? sponsorLogo(sponsor) : null;
  const logo = logoUrl && !(material?.kind === 'image' && !graphic && material.url === logoUrl) ? logoUrl : null;
  const color = useSponsorColor(sponsor);
  const look = ads.lshape.look === 'sponsor' && !color ? 'dark' : ads.lshape.look;
  const headline = sponsor ? sponsor.tagline || (logo ? sponsor.name : '') : '';
  const cta = sponsor ? sponsor.cta || (sponsor.link ? linkLabel(sponsor.link) : '') : '';
  const slot = at ? `${at.round}:${at.index}` : '';
  const held = scene.frame.held;

  return (
    <div
      className="adframe adframe--lshape"
      data-look={look}
      style={look === 'sponsor' && color ? ({ '--af-color': color, '--af-ink': inkFor(color) } as CSSProperties) : undefined}
      role="complementary"
      aria-label="Werbung"
    >
      <div className="adframe__ground" />
      <div className="adframe__spot">
        <div className="adframe__screen">
          {at && !graphic && (
            <LShapeCorner item={at.item} slot={slot} offsetMs={at.offsetMs} held={held} onEnded={() => tick((n) => n + 1)} />
          )}
        </div>
      </div>
      <div className="adframe__side">
        {sponsor && (
          <div className="adframe__brand af-swap" key={`${sponsor.id}:${logo ? 'logo' : 'name'}`}>
            {logo ? (
              <PlatedLogo url={logo} name={sponsor.name} plate={ads.logo.plate} />
            ) : (
              <>
                <span className="adframe__kicker">Mit freundlicher Unterstützung von</span>
                <b className="adframe__name">{sponsor.name}</b>
              </>
            )}
          </div>
        )}
      </div>
      <div className="adframe__base">
        {/* Eigene Ebene, damit sie sich an der Form des Streifens ausrichten kann (Stylesheet). */}
        <div className="adframe__basebox">
          <div className="adframe__copy">
            <div className="adframe__meta">
              <span className="adframe__tag">Werbung</span>
              {items.length > 1 && (
                <span className="adframe__dots" aria-hidden>
                  {items.map((item, i) => (
                    <i key={`${item.material.id}:${i}`} className={i === at?.index ? 'is-on' : undefined} />
                  ))}
                </span>
              )}
            </div>
            {sponsor && headline && (
              <span
                className={`adframe__claim af-swap${headline.length > 34 ? ' is-long' : ''}`}
                key={`${sponsor.id}:${headline}`}
              >
                {headline}
              </span>
            )}
            {at && <LShapeProgress key={slot} ms={at.item.ms} offsetMs={at.offsetMs} held={held} />}
          </div>
          {sponsor && (cta || sponsor.link) && (
            <div className="adframe__cta af-swap" key={`${sponsor.id}:${cta}:${sponsor.link}`}>
              {cta && <span className="adframe__cta-text">{breakable(cta)}</span>}
              {sponsor.link && (
                <span className="adframe__qr">
                  <QrCode value={sponsor.link} />
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Steht immer im Baum und blendet nur ueber -- so wechselt es weich zwischen Grafik und Spot. */}
      <div className={`adframe__graphic${graphic ? ' is-on' : ''}`} aria-hidden={graphic ? undefined : true}>
        {graphic && <img src={graphic.material.url} alt={graphic.sponsor.name} draggable={false} />}
        <span className="adframe__tag">Werbung</span>
      </div>
    </div>
  );
}

/** Eine Adresse darf nach „/" und „." umbrechen, nicht mitten im Wort. */
function breakable(text: string) {
  return text.split(/(?<=[/.])/).map((part, i) => (
    <span key={i}>
      {i > 0 && <wbr />}
      {part}
    </span>
  ));
}

/**
 * Wie lange das Stueck noch steht -- eine CSS-Animation, die beim Einhaengen
 * an die richtige Stelle springt (negativer Beginn). Neu eingehaengt wird
 * sie nur fuer ein neues Stueck; die Stelle danach nachzureichen, liesse sie
 * doppelt zaehlen. Gehalten bleibt sie stehen.
 */
function LShapeProgress({ ms, offsetMs, held }: { ms: number; offsetMs: number; held: boolean }) {
  const [start] = useState(offsetMs);
  return (
    <span
      className="adframe__progress"
      data-held={held ? 'on' : 'off'}
      style={{ '--af-item-ms': `${Math.max(1, ms)}ms`, '--af-item-at': `${-start}ms` } as CSSProperties}
    />
  );
}

/** Was in der Ecke steht: ein stummes Video oder ein Eckbild auf seiner eigenen Unschaerfe. */
function LShapeCorner({
  item,
  slot,
  offsetMs,
  held,
  onEnded,
}: {
  item: LShapeItem;
  /** Neu fuer jedes Stueck und jede Runde -- dann wird neu geladen. */
  slot: string;
  offsetMs: number;
  held: boolean;
  onEnded: () => void;
}) {
  const { material } = item;
  if (material.kind === 'image') {
    return (
      <div className="adframe__still" style={{ '--still': `url("${material.url}")` } as CSSProperties}>
        <img src={material.url} alt={item.sponsor.name} draggable={false} />
      </div>
    );
  }
  return <LShapeSpot slot={`${slot}:${material.url}`} url={material.url} offsetMs={offsetMs} loop={held} onEnded={onEnded} />;
}

/**
 * Der stumme Spot in der Ecke. Stumm darf jeder Browser ohne Tipp abspielen,
 * auch iOS -- anders als die Werbepause braucht er deshalb kein Element, das
 * schon beim Tipp auf „Ton an" da war. Gehalten laeuft er in Schleife.
 */
function LShapeSpot({
  slot,
  url,
  offsetMs,
  loop,
  onEnded,
}: {
  slot: string;
  url: string;
  offsetMs: number;
  loop: boolean;
  onEnded: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const offsetRef = useRef(offsetMs);
  offsetRef.current = offsetMs;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    el.src = url;
    const seek = () => {
      const at = offsetRef.current / 1000;
      if (at > 0.5 && Number.isFinite(el.duration) && at < el.duration - 0.25) el.currentTime = at;
    };
    el.addEventListener('loadedmetadata', seek, { once: true });
    void el.play().catch(() => undefined);
    return () => {
      el.removeEventListener('loadedmetadata', seek);
      el.pause();
      el.removeAttribute('src');
      el.load();
    };
  }, [slot, url]);

  return <video ref={ref} className="adframe__video" muted playsInline preload="auto" loop={loop} onEnded={onEnded} />;
}
